import { useCallback, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { FantaExportCard } from "./FantaExportCard";
import { FantaPredictionsList } from "./FantaPredictionsList";
import { FantaRecap } from "./FantaRecap";
import { toast } from "sonner";
import { Loader2, Gamepad2, Trophy, Check, Users, Target, Share2, Download, Info } from "lucide-react";

const NICK_KEY = "ap_fanta_nick";

const emptyPick = () => ({ s1: { g1: "", g2: "" }, s2: { g1: "", g2: "" }, s3: { g1: "", g2: "" } });

function matchStatus(pick) {
  if (!pick) return { valid: false, sets: [] };
  const rows = [pick.s1, pick.s2, pick.s3];
  let w1 = 0, w2 = 0;
  const sets = [];
  for (const s of rows) {
    if (!s || s.g1 === "" || s.g2 === "") break;
    const g1 = Number(s.g1), g2 = Number(s.g2);
    if (Number.isNaN(g1) || Number.isNaN(g2) || g1 === g2) return { valid: false, sets };
    sets.push({ g1, g2 });
    if (g1 > g2) w1++; else w2++;
    if (w1 === 2 || w2 === 2) {
      return { valid: true, winner: w1 === 2 ? "team1" : "team2", sets, w1, w2 };
    }
  }
  return { valid: false, sets };
}

function pickToState(stored) {
  const p = emptyPick();
  const sets = (stored && stored.sets) || [];
  ["s1", "s2", "s3"].forEach((k, i) => {
    if (sets[i]) p[k] = { g1: String(sets[i].g1), g2: String(sets[i].g2) };
  });
  return p;
}

function ScoreInput({ value, onChange, testid }) {
  return (
    <input
      data-testid={testid}
      inputMode="numeric"
      maxLength={1}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-10 h-10 text-center bg-slate-900 border border-slate-700 rounded-lg text-sm font-mono text-slate-100 focus:outline-none focus:ring-1 focus:ring-fuchsia-500"
    />
  );
}

export function FantaPadelView() {
  const [cur, setCur] = useState(null);
  const [board, setBoard] = useState(null);
  const [nick, setNick] = useState(localStorage.getItem(NICK_KEY) || "");
  const [picks, setPicks] = useState({});
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [predRefresh, setPredRefresh] = useState(0);
  const exportRef = useRef(null);

  const loadBoard = useCallback(async () => {
    const r = await api.get("/fantapadel/leaderboard");
    setBoard(r.data.rows);
  }, []);

  const loadMine = useCallback(async (n, md) => {
    if (!n || n.trim().length < 2) { setSaved(false); return; }
    try {
      const r = await api.get("/fantapadel/mine", { params: { nickname: n.trim(), matchday: md } });
      if (r.data.found) {
        const conv = {};
        Object.entries(r.data.picks || {}).forEach(([court, val]) => { conv[court] = pickToState(val); });
        setPicks(conv);
        setSaved(true);
      } else {
        setSaved(false);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    api.get("/fantapadel/current").then((r) => {
      setCur(r.data);
      if (r.data.open && nick.trim().length >= 2) loadMine(nick, r.data.matchday);
    });
    loadBoard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setGame = (court, setKey, side, value) => {
    if (saving) return;
    const v = value === "" ? "" : String(value).replace(/[^0-9]/g, "").slice(0, 1);
    setSaved(false);
    setPicks((prev) => {
      const cs = prev[String(court)] || emptyPick();
      return { ...prev, [String(court)]: { ...cs, [setKey]: { ...cs[setKey], [side]: v } } };
    });
  };

  const statuses = cur?.open ? cur.matches.map((m) => matchStatus(picks[String(m.court)])) : [];
  const allValid = cur?.open && statuses.length === cur.matches.length && statuses.every((s) => s.valid);

  const buildPayload = () => {
    const payload = {};
    cur.matches.forEach((m) => {
      const st = matchStatus(picks[String(m.court)]);
      payload[String(m.court)] = { winner: st.winner, sets: st.sets };
    });
    return payload;
  };

  const submit = async () => {
    if (!nick || nick.trim().length < 2) { toast.error("Inserisci un nickname (min 2 caratteri)"); return; }
    if (!allValid) { toast.error("Completa il risultato (2-0 o 2-1) per tutti i campi"); return; }
    setSaving(true);
    try {
      await api.post("/fantapadel/predict", { nickname: nick.trim(), matchday: cur.matchday, picks: buildPayload() });
      localStorage.setItem(NICK_KEY, nick.trim());
      toast.success("Pronostico salvato! In bocca al lupo 🎾");
      setSaved(true);
      const c = await api.get("/fantapadel/current");
      setCur(c.data);
      setPredRefresh((n) => n + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const renderCanvas = async () => {
    if (!exportRef.current) return null;
    return html2canvas(exportRef.current, { backgroundColor: "#0B0F19", scale: 2, logging: false });
  };

  const shareFanta = async () => {
    setSharing(true);
    try {
      const canvas = await renderCanvas();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "pronostico-american-padel.png", { type: "image/png" });
      const text = `Il mio pronostico Fantapadel — Giornata ${cur.matchday} 🎾 Gioca anche tu: ${window.location.origin}`;
      const canShareFiles = typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
      if (canShareFiles) {
        try {
          await navigator.share({ files: [file], title: "Il mio pronostico Fantapadel", text });
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = "pronostico-american-padel.png";
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Immagine salvata (condivisione diretta non disponibile)");
    } catch {
      toast.error("Errore durante la condivisione");
    } finally { setSharing(false); }
  };

  const downloadFanta = async () => {
    setSharing(true);
    try {
      const canvas = await renderCanvas();
      const link = document.createElement("a");
      link.download = "pronostico-american-padel.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Immagine scaricata");
    } catch {
      toast.error("Errore durante l'esportazione");
    } finally { setSharing(false); }
  };

  if (!cur || board === null) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>;
  }

  const exportPicks = {};
  if (cur.open) {
    cur.matches.forEach((m) => {
      const st = matchStatus(picks[String(m.court)]);
      if (st.valid) exportPicks[String(m.court)] = { winner: st.winner, sets: st.sets };
    });
  }

  return (
    <div className="space-y-6" data-testid="fantapadel-view">
      {cur.open && saved && (
        <FantaExportCard
          ref={exportRef}
          matchday={cur.matchday}
          date={cur.date}
          nickname={nick.trim()}
          matches={cur.matches}
          picks={exportPicks}
          appUrl={window.location.origin}
        />
      )}
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-fuchsia-400">Gioca con noi</div>
        <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white">
          Fantapadel
        </h2>
        <p className="text-sm text-slate-400 mt-1">Pronostica il risultato esatto (con i game) di ogni campo: 1 punto per il vincitore, +2 bonus se indovini il risultato.</p>
      </div>

      <FantaRecap nickname={nick} refreshKey={predRefresh} />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Prediction form */}
        <div className="ap-card p-5" data-testid="fanta-predict-section">
          <div className="flex items-center gap-2 mb-4">
            <Gamepad2 className="w-5 h-5 text-fuchsia-400" />
            <h3 className="font-display uppercase tracking-wide text-lg text-white">
              {cur.open ? `Pronostici · Giornata ${cur.matchday}` : "Pronostici"}
            </h3>
          </div>

          {!cur.open ? (
            <div className="text-center py-10 text-slate-500">
              <Target className="w-10 h-10 mx-auto mb-3 text-slate-600" />
              Nessuna giornata aperta ai pronostici al momento. Torna presto!
            </div>
          ) : (
            <div className="space-y-4">
              <input
                data-testid="fanta-nick-input"
                value={nick}
                onChange={(e) => { setNick(e.target.value); setSaved(false); }}
                onBlur={() => loadMine(nick, cur.matchday)}
                placeholder="Il tuo nickname"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-fuchsia-500"
              />

              {cur.matches.map((m) => {
                const st = matchStatus(picks[String(m.court)]);
                const court = m.court;
                const winnerNames = st.valid ? (st.winner === "team1" ? m.team1 : m.team2).join(" / ") : null;
                return (
                  <div key={court} className="rounded-xl bg-slate-950/40 border border-slate-800 p-3" data-testid={`fanta-match-${court}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-widest text-slate-500">Campo {court}</span>
                      {st.valid ? (
                        <span data-testid={`fanta-result-${court}`} className="text-[11px] font-semibold text-emerald-400 truncate max-w-[60%] text-right">
                          {winnerNames} {st.w1}-{st.w2}
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-600">risultato incompleto</span>
                      )}
                    </div>

                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-2 gap-y-1.5 items-center">
                      <div />
                      <div className="text-center text-[9px] uppercase tracking-wider text-slate-500 w-10">Set 1</div>
                      <div className="text-center text-[9px] uppercase tracking-wider text-slate-500 w-10">Set 2</div>
                      <div className="text-center text-[9px] uppercase tracking-wider text-slate-500 w-10">Set 3</div>

                      <div className={`text-xs font-semibold truncate pr-1 ${st.winner === "team1" ? "text-emerald-400" : "text-slate-300"}`}>
                        {m.team1.join(" / ")}
                      </div>
                      <ScoreInput value={picks[String(court)]?.s1?.g1} onChange={(v) => setGame(court, "s1", "g1", v)} testid={`fanta-score-${court}-1-t1`} />
                      <ScoreInput value={picks[String(court)]?.s2?.g1} onChange={(v) => setGame(court, "s2", "g1", v)} testid={`fanta-score-${court}-2-t1`} />
                      <ScoreInput value={picks[String(court)]?.s3?.g1} onChange={(v) => setGame(court, "s3", "g1", v)} testid={`fanta-score-${court}-3-t1`} />

                      <div className={`text-xs font-semibold truncate pr-1 ${st.winner === "team2" ? "text-emerald-400" : "text-slate-300"}`}>
                        {m.team2.join(" / ")}
                      </div>
                      <ScoreInput value={picks[String(court)]?.s1?.g2} onChange={(v) => setGame(court, "s1", "g2", v)} testid={`fanta-score-${court}-1-t2`} />
                      <ScoreInput value={picks[String(court)]?.s2?.g2} onChange={(v) => setGame(court, "s2", "g2", v)} testid={`fanta-score-${court}-2-t2`} />
                      <ScoreInput value={picks[String(court)]?.s3?.g2} onChange={(v) => setGame(court, "s3", "g2", v)} testid={`fanta-score-${court}-3-t2`} />
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-slate-500">
                      <Info className="w-3 h-3" /> Compila il Set 3 solo in caso di 2-1.
                    </div>
                  </div>
                );
              })}

              <div className="flex items-center justify-between gap-3 pt-1">
                <span className="text-xs text-slate-500 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> {cur.participants} partecipanti
                </span>
                <Button
                  data-testid="fanta-submit-btn"
                  onClick={submit}
                  disabled={saving || !allValid}
                  className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-bold uppercase text-xs tracking-wide disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? (<><Check className="w-4 h-4 mr-1.5" />Salvato · aggiorna</>) : "Invia pronostico"}
                </Button>
              </div>

              {saved && (
                <div className="space-y-2 pt-1">
                  <p className="text-xs text-emerald-400 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" />Pronostico registrato. Puoi modificarlo finché la giornata è aperta.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="fanta-share-btn"
                      onClick={shareFanta}
                      disabled={sharing}
                      size="sm"
                      className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-bold uppercase text-xs tracking-wide"
                    >
                      {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4 sm:mr-1.5" />}
                      <span className="hidden sm:inline">Condividi pronostico</span>
                      <span className="sm:hidden">Condividi</span>
                    </Button>
                    <Button
                      data-testid="fanta-download-btn"
                      onClick={downloadFanta}
                      disabled={sharing}
                      size="sm"
                      className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 font-semibold uppercase text-xs tracking-wide"
                    >
                      <Download className="w-4 h-4 sm:mr-1.5" />
                      <span className="hidden sm:inline">Scarica</span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="ap-card p-5" data-testid="fanta-leaderboard-section">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-amber-400" />
            <h3 className="font-display uppercase tracking-wide text-lg text-white">Classifica Tifosi</h3>
          </div>
          {board.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">
              Ancora nessun pronostico valutato. Sii il primo a giocare!
            </div>
          ) : (
            <div className="space-y-2">
              {board.map((r) => (
                <div key={r.nickname} data-testid={`fanta-board-row-${r.rank}`} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-950/50 border border-slate-800">
                  <span className={`w-6 h-6 rounded-lg flex items-center justify-center font-display font-black text-xs shrink-0 ${
                    r.rank === 1 ? "bg-amber-500 text-slate-950" : r.rank === 2 ? "bg-slate-400 text-slate-950" : r.rank === 3 ? "bg-amber-700 text-white" : "bg-slate-800 text-slate-400"
                  }`}>{r.rank}</span>
                  <span className="text-sm text-slate-200 truncate flex-1">{r.nickname}</span>
                  <span className="font-mono text-[11px] text-slate-500 shrink-0">{r.exact} esatti · {r.accuracy}%</span>
                  <span className="font-mono font-black text-lg text-emerald-400 shrink-0">{r.points}</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-600 mt-3 leading-snug">
            Punteggio: +1 per il vincitore giusto, +2 bonus per il risultato esatto. A parità di punti conta chi ha più risultati esatti.
          </p>
        </div>
      </div>

      <FantaPredictionsList refreshKey={predRefresh} />
    </div>
  );
}
